import React, { useState } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCompany,
  usePatchCompany,
  usePatchCompanyAddress,
} from "../../hooks/useCompany";
import {
  CompanyCreateDTO,
  AddressCreateDTO
} from "../../models/Company";
import CompanyForm, { TranslatableFormData } from "../../components/company/CompanyForm";
import { LanguageCode } from "../../models/enums/LanguageCode";

const CompanyEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const companyId = parseInt(id || "0");

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: company, isLoading, error } = useCompany(companyId);
  const patchCompanyMutation = usePatchCompany();
  const patchAddressMutation = usePatchCompanyAddress();

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error">(
    "success"
  );

  const handleUpdateCompany = async (
    companyData: CompanyCreateDTO,
    addressData?: AddressCreateDTO,
    translatableData?: TranslatableFormData
  ) => {
    if (!company) return;

    try {
      // Build patch operations for non-translatable fields
      const patchOperations = [];

      if (companyData.legalName !== company.legalName) {
        patchOperations.push({
          op: "replace",
          path: "/legalName",
          value: companyData.legalName,
        });
      }

      if (companyData.tradingName !== company.tradingName) {
        patchOperations.push({
          op: "replace",
          path: "/tradingName",
          value: companyData.tradingName,
        });
      }

      if (companyData.sectorId !== company.sector?.id) {
        patchOperations.push({
          op: "replace",
          path: "/sectorId",
          value: companyData.sectorId,
        });
      }

      if (companyData.companySize !== company.companySize) {
        patchOperations.push({
          op: "replace",
          path: "/companySize",
          value: companyData.companySize,
        });
      }

      if (companyData.companyType !== company.companyType) {
        patchOperations.push({
          op: "replace",
          path: "/companyType",
          value: companyData.companyType,
        });
      }

      if (companyData.website !== company.website) {
        patchOperations.push({
          op: "replace",
          path: "/website",
          value: companyData.website,
        });
      }

      // Check if company group changed
      const currentGroupId = company.companyGroup?.id ?? null;
      const newGroupId = companyData.companyGroupId ?? null;
      if (newGroupId !== currentGroupId) {
        patchOperations.push({
          op: "replace",
          path: "/companyGroupId",
          value: newGroupId,
        });
      }

      // Handle translatable fields per language
      if (translatableData) {
        const translatableFields = ["description", "market", "geographicPresence"];

        for (const { languageCode, fields } of translatableData.modifiedLanguages) {
          const langPatchOps = [];

          for (const field of translatableFields) {
            if (fields[field] !== undefined) {
              langPatchOps.push({
                op: "replace",
                path: `/${field}`,
                value: fields[field],
              });
            }
          }

          if (langPatchOps.length > 0) {
            if (languageCode === LanguageCode.Fr) {
              // FR operations go with the main patch (no languageCode param)
              patchOperations.push(...langPatchOps);
            } else {
              // Non-primary language: send a separate PATCH with languageCode
              await patchCompanyMutation.mutateAsync({
                id: companyId,
                operations: langPatchOps,
                languageCode: languageCode.toLowerCase(),
              });
            }
          }
        }
      }

      // Apply main company updates (including FR translatable fields)
      if (patchOperations.length > 0) {
        await patchCompanyMutation.mutateAsync({
          id: companyId,
          operations: patchOperations,
        });
      }

      // Update address if changed and headquarters address exists
      if (addressData) {
        // Find headquarters address if it exists
        const headquartersAddress = company.addresses?.find(
          addr => addr.addressType.code === 'HEADQUARTERS' && addr.isDefault
        );

        if (headquartersAddress) {
          // Create patch operations for address (only for fields that changed)
          const addressPatchOperations = [];

          if (addressData.fullAddress !== headquartersAddress.address.fullAddress) {
            addressPatchOperations.push({
              op: "replace",
              path: "/fullAddress",
              value: addressData.fullAddress,
            });
          }

          if (addressData.street !== headquartersAddress.address.street) {
            addressPatchOperations.push({
              op: "replace",
              path: "/street",
              value: addressData.street,
            });
          }

          if (addressData.streetNumber !== headquartersAddress.address.streetNumber) {
            addressPatchOperations.push({
              op: "replace",
              path: "/streetNumber",
              value: addressData.streetNumber,
            });
          }

          if (addressData.postalCode !== headquartersAddress.address.postalCode) {
            addressPatchOperations.push({
              op: "replace",
              path: "/postalCode",
              value: addressData.postalCode,
            });
          }

          if (addressData.city !== headquartersAddress.address.city) {
            addressPatchOperations.push({
              op: "replace",
              path: "/city",
              value: addressData.city,
            });
          }

          if (addressData.region !== headquartersAddress.address.region) {
            addressPatchOperations.push({
              op: "replace",
              path: "/region",
              value: addressData.region,
            });
          }

          if (addressData.country !== headquartersAddress.address.country) {
            addressPatchOperations.push({
              op: "replace",
              path: "/country",
              value: addressData.country,
            });
          }

          if (addressData.countryCode !== headquartersAddress.address.countryCode) {
            addressPatchOperations.push({
              op: "replace",
              path: "/countryCode",
              value: addressData.countryCode,
            });
          }

          if (addressData.latitude !== headquartersAddress.address.latitude) {
            addressPatchOperations.push({
              op: "replace",
              path: "/latitude",
              value: addressData.latitude,
            });
          }

          if (addressData.longitude !== headquartersAddress.address.longitude) {
            addressPatchOperations.push({
              op: "replace",
              path: "/longitude",
              value: addressData.longitude,
            });
          }

          if (addressData.placeId !== headquartersAddress.address.placeId) {
            addressPatchOperations.push({
              op: "replace",
              path: "/placeId",
              value: addressData.placeId,
            });
          }

          // Apply address updates if there are any changes
          if (addressPatchOperations.length > 0) {
            await patchAddressMutation.mutateAsync({
              companyId,
              addressId: headquartersAddress.address.id,
              operations: addressPatchOperations,
            });
          }
        }
      }

      setAlertSeverity("success");
      setAlertMessage(t("company.updatedSuccessfully"));
      setAlertOpen(true);

      // Navigate back after short delay
      setTimeout(() => {
        navigate(`/companies/${companyId}`);
      }, 1500);
    } catch (error) {
      console.error("Error updating company:", error);
      setAlertSeverity("error");
      setAlertMessage(t("company.updateError"));
      setAlertOpen(true);
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleNavigateToDetails = () => {
    navigate(`/companies/${companyId}`);
  };

  const handleNavigateToList = () => {
    navigate("/companies");
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box p={3}>
        <Typography color="error">
          {t("company.errorLoadingCompany")}:{" "}
          {error?.message || t("company.notFound")}
        </Typography>
      </Box>
    );
  }

  // Find headquarters address if it exists
  const headquartersAddress = company.addresses?.find(
    addr => addr.addressType.code === 'HEADQUARTERS' && addr.isDefault
  );

  return (
    <Box p={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h2">{t("company.editCompany")}</Typography>
      </Box>

      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToList}
          sx={{ cursor: "pointer" }}
        >
          {t("company.companyList")}
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={handleNavigateToDetails}
          sx={{ cursor: "pointer" }}
        >
          {company.legalName}
        </Link>
        <Typography color="text.primary">{t("common.edit")}</Typography>
      </Breadcrumbs>

      <CompanyForm
        initialData={{
          ...company,
          sectorId: company.sector?.id
        }}
        initialAddress={headquartersAddress?.address}
        initialCompanyGroup={company.companyGroup}
        onSubmit={handleUpdateCompany}
        isSubmitting={
          patchCompanyMutation.isPending || patchAddressMutation.isPending
        }
        submitButtonText={t("common.save")}
        companyId={companyId}
      />

      <Snackbar
        open={alertOpen}
        autoHideDuration={5000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleAlertClose}
          severity={alertSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyEdit;